import { DEFAULT_TOKEN } from '@/lib/enums'
import { patchTicketStatusInList, resolveTicketHistoryErrorDuringFollowUp } from '@/lib/ticket-follow-up'
import { normalizeTicketUpdateBadge } from '@/lib/ticket-update-badge'
import { normalizeLiveSupportState } from '@/lib/live-support'
import { useChatContext } from '@/context/ChatProvider'
import { mergeConversationMessage, sessionClosureMessageId } from '@/lib/conversation-message-state'
import { useChatStore } from '@/store/chat.store'
import { usePersistStore } from '@/store/persist.store'
import { useSocketStore } from '@/store/socket.store'
import { useAuthHandlers } from './useAuthHandlers'
import { useMessageHandlers } from './useMessageHandlers'
import { useTranslations } from 'next-intl'

export const useSocketHandlers = () => {
  const t = useTranslations('chat')
  const { scrollToBottom } = useChatContext()
  const authHandlers = useAuthHandlers()
  const messageHandlers = useMessageHandlers()

  const setSessionId = usePersistStore(state => state.setSessionId)
  const setMessages = useChatStore(state => state.setMessages)
  const setToken = usePersistStore(state => state.setToken)
  const setIsGuestIdActive = usePersistStore(state => state.setIsGuestIdActive)
  const resetStore = usePersistStore(state => state.resetStore)
  const setIsTakenOver = usePersistStore(state => state.setIsTakenOver)
  const setOperatorName = useChatStore(state => state.setOperatorName)
  const setLiveSupportState = useChatStore(state => state.setLiveSupportState)
  const setIsChatEnded = useChatStore(state => state.setIsChatEnded)

  const checkAuthErrorCode = (code: number) => {
    if (code === 401 || code === 404) {
      setIsGuestIdActive(true)
      setToken(DEFAULT_TOKEN)
    } else if (code === 403) {
      setSessionId(null)
    } else if (code === 500) {
      resetStore()
    }
  }

  const handleAuthError = (data: any) => {
    const addErrorMessage = (message: string) => {
      setMessages((prev: any) => [
        ...prev,
        {
          role: 'assistant',
          content: message ? (/\p{P}$/u.test(message) ? message : `${message}.`) : t('authErrorTitle'),
          type: 'error'
        }
      ])
    }

    checkAuthErrorCode(data?.error?.code)

    authHandlers.handleAuthError(data, addErrorMessage)
  }

  const handleAuthFailed = (data: any) => {
    const addErrorMessage = (message: string) => {
      setMessages((prev: any) => [
        ...prev,
        {
          role: 'assistant',
          content: message ? (/\p{P}$/u.test(message) ? message : `${message}.`) : t('authErrorTitle'),
          type: 'error'
        }
      ])
    }

    checkAuthErrorCode(data?.error?.code)

    authHandlers.handleAuthFailed(data, addErrorMessage)
  }

  const handleSessionUpdate = (data: any) => {
    setIsTakenOver(data.is_taken_over || false)

    // A direct takeover ends without an operator_left/resolved notification,
    // so clear the operator banner when the takeover is released mid-assist.
    // Queue releases keep working: their operator_left notification re-sets
    // the pending state regardless of frame order.
    if (data.is_taken_over === false) {
      const { liveSupportState } = useChatStore.getState()
      if (liveSupportState?.status === 'processing') {
        setOperatorName(null)
        setLiveSupportState(null)
      }
    }
  }

  const handleSessionState = (data: any) => {
    const liveSupportState = normalizeLiveSupportState(data?.live_support ?? data?.data?.live_support)

    setLiveSupportState(liveSupportState)
    setOperatorName(liveSupportState?.operator_name || null)
  }

  const handleEnd = () => {
    setIsChatEnded(true)
  }

  const handleSessionClosed = (data: any) => {
    const sessionId = usePersistStore.getState().sessionId
    if (data?.session_id && data.session_id !== sessionId) return

    const message =
      typeof data?.message === 'string' && data.message.trim() ? data.message.trim() : t('chatEnded')
    setIsTakenOver(false)
    setOperatorName(null)
    setLiveSupportState(null)
    setMessages(prev =>
      mergeConversationMessage(
        prev.filter(message => !message.isThinking),
        {
          _id: sessionClosureMessageId(sessionId),
          role: 'assistant',
          content: message,
          type: 'info',
          timestamp: Date.now()
        }
      )
    )
    setIsChatEnded(true)
    scrollToBottom()
  }

  const handleUploadFileSuccess = () => {
    const ack = useSocketStore.getState().pendingUploadFileAck
    useSocketStore.getState().setPendingUploadFileAck(null)
    ack?.resolve()
  }

  const handleUploadFileError = (data: any) => {
    const ack = useSocketStore.getState().pendingUploadFileAck
    useSocketStore.getState().setPendingUploadFileAck(null)
    const message = data?.message || data?.content || t('fileUploadFailed')
    ack?.reject(new Error(message))
  }

  const handleNewTopicSuccess = () => {
    const ack = useSocketStore.getState().pendingNewTopicAck
    useSocketStore.getState().setPendingNewTopicAck(null)
    ack?.resolve()
    if (!ack) setIsChatEnded(true)
  }

  const handleNewTopicError = (data: any) => {
    const ack = useSocketStore.getState().pendingNewTopicAck
    useSocketStore.getState().setPendingNewTopicAck(null)
    const message = data?.message || data?.content || t('connectionFailed')
    ack?.reject(new Error(message))
  }

  // Ticket notifications arrive as generic notification frames; bumping the
  // refresh key lets a mounted tickets panel refetch its list.
  const handleNotification = (data: any) => {
    messageHandlers.handleNotification(data)
    useChatStore.getState().bumpTicketsRefreshKey()
  }

  const handleTicketsList = (data: any) => {
    const { setTickets, setIsLoadingTickets } = useChatStore.getState()
    setTickets(Array.isArray(data?.tickets) ? data.tickets : [])
    setIsLoadingTickets(false)
  }

  const handleTicketHistory = (data: any) => {
    const store = useChatStore.getState()
    const {
      setActiveTicket,
      setIsLoadingTicketHistory,
      setTicketHistoryError,
      setIsSendingTicketFollowUp,
      setTicketFollowUpError,
      setTickets,
      bumpTicketsRefreshKey,
      tickets,
      isSendingTicketFollowUp
    } = store

    if (data?.ticket?.id) {
      setActiveTicket({ ticket: data.ticket, messages: Array.isArray(data?.messages) ? data.messages : [] })
      setTicketHistoryError(false)

      if (isSendingTicketFollowUp) {
        if (tickets.length > 0) {
          setTickets(patchTicketStatusInList(tickets, data.ticket.id, data.ticket))
        }
        bumpTicketsRefreshKey()
      }
    } else {
      setTicketHistoryError(true)
    }
    setIsLoadingTicketHistory(false)
    setIsSendingTicketFollowUp(false)
    if (isSendingTicketFollowUp) {
      setTicketFollowUpError(!data?.ticket?.id)
    } else {
      setTicketFollowUpError(false)
    }
  }

  const handleTicketFollowUpError = () => {
    const { setIsSendingTicketFollowUp, setTicketFollowUpError } = useChatStore.getState()
    setTicketFollowUpError(true)
    setIsSendingTicketFollowUp(false)
  }

  const handleTicketHistoryError = () => {
    const store = useChatStore.getState()
    const {
      isSendingTicketFollowUp,
      setIsLoadingTicketHistory,
      setTicketHistoryError,
      setIsSendingTicketFollowUp,
      setTicketFollowUpError
    } = store
    const resolution = resolveTicketHistoryErrorDuringFollowUp(isSendingTicketFollowUp)

    setTicketHistoryError(true)
    setIsLoadingTicketHistory(false)

    if (resolution.clearSending) {
      setIsSendingTicketFollowUp(false)
    }
    if (resolution.markFollowUpError) {
      setTicketFollowUpError(true)
    }
  }

  const handleTicketUpdates = (data: any) => {
    const { count, cursor } = normalizeTicketUpdateBadge(data)
    const store = useChatStore.getState()
    store.setTicketUpdateBadge(count, cursor)
    if (count > 0) store.bumpTicketsRefreshKey()
  }

  return {
    ...authHandlers,
    ...messageHandlers,
    handleAuthError,
    handleAuthFailed,
    handleSessionUpdate,
    handleSessionState,
    handleEnd,
    handleSessionClosed,
    handleUploadFileSuccess,
    handleUploadFileError,
    handleNewTopicSuccess,
    handleNewTopicError,
    handleNotification,
    handleTicketsList,
    handleTicketHistory,
    handleTicketFollowUpError,
    handleTicketHistoryError,
    handleTicketUpdates
  }
}
